import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import { fetchClient } from "../../API/fetchClient";
import {
  useGetEauKpis,
  type KpisFilters,
  type KpisAnalyticsResponse,
} from "./useGetKpisAnalytics";
import { useGetSites } from "../site/useGetSites";

// ─── Types ───
export interface RatioItem {
  name: string;
  m3: number;
  ha: number;
  ratio: number;
}

export interface EvoConsoItem {
  month: string;
  ratio: number; // m3/ha from rapport_surface
  total_m3: number;
}

export interface SiteSplitItem {
  siteName: string;
  ratio: number; // m3/ha from rapport_surface per site
  total_m3: number;
  ha: number;
}

export interface EauRatiosData {
  ratioFamille: RatioItem[];
  ratioProduit: RatioItem[];
  evoConsoEau: EvoConsoItem[];
  consoSplitSite: SiteSplitItem[];
  isLoading: boolean;
  hasError: boolean;
}

// ─── Hook ───
export const useGetEauRatios = (filters: KpisFilters, enabled = true): EauRatiosData => {
  const { data: sites = [] } = useGetSites();

  // ── 1) Water by famille (m³) from backend — EAU_FAM_* ────────────────────
  const { data: eauFamilleData, isLoading: famLoading, error: famError } = useGetEauKpis(
    { ...filters, dimension_type: "famille" },
    enabled
  );

  // ── 2) Water by produit (m³) from backend — EAU_PROD_* ───────────────────
  const { data: eauProduitData, isLoading: prodLoading, error: prodError } = useGetEauKpis(
    { ...filters, dimension_type: "produit" },
    enabled
  );

  // ── 3) Global rapport_surface (m³/ha) — time series for evo chart ────────
  const { data: rapportSurfaceData, isLoading: rapportSurfaceLoading } = useGetEauKpis(
    { ...filters, dimension_type: "rapport_surface" },
    enabled
  );

  // ── 4) Per-site rapport_surface (m³/ha) for consoSplitSite ───────────────
  const siteIds = sites.map((s) => s.id);

  const perSiteRapportSurfaceResults = useQueries({
    queries: siteIds.map((siteId) => ({
      queryKey: ["rapportSurfacePerSite", siteId, filters],
      queryFn: async () => {
        const body: Record<string, unknown> = {
          ...filters,
          site_ids: [siteId],
          dimension_type: "rapport_surface",
        };
        const res = await fetchClient<KpisAnalyticsResponse>("/core/analytics/eau/", {
          method: "POST",
          body,
        });
        const ratio = res.data?.summary[0]?.avg ?? 0;
        return { siteId, ratio };
      },
      enabled: enabled && !!filters.from_period && !!filters.to_period && siteIds.length > 0,
      staleTime: 1000 * 60 * 5,
    })),
  });

  // ── Build ratio items directly from backend m³ values ────────────────────

  const ratioFamille = useMemo((): RatioItem[] => {
    if (!eauFamilleData?.summary) return [];
    return eauFamilleData.summary
      .map((item) => ({
        name: item.name.replace("Eau - ", ""),
        m3: item.total,
        ha: 0,
        ratio: item.total,
      }))
      .filter((item) => item.ratio > 0)
      .sort((a, b) => b.ratio - a.ratio);
  }, [eauFamilleData]);

  const ratioProduit = useMemo((): RatioItem[] => {
    if (!eauProduitData?.summary) return [];
    return eauProduitData.summary
      .map((item) => ({
        name: item.name.replace("Eau - ", ""),
        m3: item.total,
        ha: 0,
        ratio: item.total,
      }))
      .filter((item) => item.ratio > 0)
      .sort((a, b) => b.ratio - a.ratio);
  }, [eauProduitData]);

  // evoConsoEau: m³/ha ratio per month directly from backend rapport_surface
  const evoConsoEau = useMemo((): EvoConsoItem[] => {
    if (!rapportSurfaceData?.time_series) return [];
    return rapportSurfaceData.time_series.map((ts) => ({
      month: ts.month as string,
      total_m3: 0,
      ratio: (ts["EAU_RATIO_SURFACE"] as number) || 0,
    }));
  }, [rapportSurfaceData]);

  // consoSplitSite: m³/ha ratio per site directly from backend rapport_surface
  const consoSplitSite = useMemo((): SiteSplitItem[] => {
    return perSiteRapportSurfaceResults
      .map((r) => r.data)
      .filter((d): d is { siteId: number; ratio: number } => !!d && d.ratio > 0)
      .map((d) => {
        const site = sites.find((s) => s.id === d.siteId);
        return {
          siteName: site?.name ?? `Site ${d.siteId}`,
          total_m3: 0,
          ha: 0,
          ratio: d.ratio,
        };
      })
      .sort((a, b) => b.ratio - a.ratio);
  }, [perSiteRapportSurfaceResults, sites]);

  return {
    ratioFamille,
    ratioProduit,
    evoConsoEau,
    consoSplitSite,
    isLoading:
      famLoading ||
      prodLoading ||
      rapportSurfaceLoading ||
      perSiteRapportSurfaceResults.some((r) => r.isLoading),
    hasError: !!famError || !!prodError,
  };
};
